import { useEffect, useState } from 'react';
import { ArrowLeft, Images } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { EmptyState, ErrorState, LoadingState } from '../components/ui';
import { Html } from '../lib/htmlContent';
import { parseFileUrl, pointer, queryAllClass, queryClass, type ParseRecord } from '../lib/parse';

type Story = ParseRecord & { StoryText?: string; Image?: unknown; createdAt?: string };

export function StudentCourseStories() {
  const { courseId = '' } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [courseTitle, setCourseTitle] = useState('Course stories');
  const [stories, setStories] = useState<Story[]>([]);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const courses = await queryClass('elearning_Course', { limit: 1, keys: ['CourseTitle', 'Stories'], where: (query) => query.equalTo('objectId', courseId) });
        if (!active) return;
        if (!courses[0]) throw new Error('This course could not be found.');
        setCourseTitle(String(courses[0].CourseTitle ?? 'Course stories'));
        setEnabled(courses[0].Stories === true);
        if (courses[0].Stories !== true) return;
        const rows = await queryAllClass('elearning_Story', {
          order: '-createdAt',
          keys: ['StoryText', 'Image', 'CoursePtr', 'createdAt'],
          where: (query) => query.equalTo('CoursePtr', pointer('elearning_Course', courseId)),
        }) as Story[];
        if (!active) return;
        setStories(rows);
      } catch (reason) {
        if (active) setError(reason instanceof Error ? reason.message : 'Course stories could not load.');
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [courseId]);

  if (loading) return <LoadingState />;
  if (error) return <div className="utility-page"><ErrorState onRetry={() => window.location.reload()} /></div>;
  return <div className="utility-page course-stories-page">
    <Link to={`/courses/${courseId}`} className="back-link"><ArrowLeft size={16} /> {courseTitle}</Link>
    <section className="utility-hero"><div className="utility-icon"><Images size={27} /></div><div><h1>{courseTitle} stories</h1><p>Updates and learning highlights shared for this course.</p></div></section>
    {!enabled ? <EmptyState icon={Images} title="Stories are not enabled" description="This course does not currently have a Stories feed." />
      : stories.length === 0 ? <EmptyState icon={Images} title="No stories yet" description="New course stories will appear here when they are published." />
        : <div className="course-stories-list">{stories.map((story) => {
          const imageUrl = parseFileUrl(story.Image);
          return <article className="course-story" key={story.objectId}>
            {imageUrl && <img src={imageUrl} alt="Course story" loading="lazy" />}
            {story.StoryText && <Html className="course-story-copy" html={story.StoryText} />}
            <time dateTime={story.createdAt}>{story.createdAt ? new Date(story.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' }) : ''}</time>
          </article>;
        })}</div>}
  </div>;
}
